/**
 * Orders Page
 * Displays orders with filters and management actions
 * Requirements: 16.2, 16.3
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ClipboardList,
  RefreshCw,
  AlertCircle,
  Loader2,
  Filter,
  X,
} from 'lucide-react';
import {
  useOrders,
  useAssignMaster,
  useDisputeOrder,
  ORDER_STATUS_LABELS,
} from '@/hooks/useOrders';
import { OrderCard } from '@/components/orders/OrderCard';
import { ResolveDisputeModal } from '@/components/orders/ResolveDisputeModal';
import { useToast } from '@/components/ui/toaster';
import type { OrderStatus, DisputeReason } from '@/lib/api';

type TabValue = 'all' | 'bidding' | 'active' | 'completed' | 'disputed';

interface TabConfig {
  value: TabValue;
  label: string;
  statuses: OrderStatus[] | null;
}

const TABS: TabConfig[] = [
  { value: 'all', label: 'Все', statuses: null },
  { value: 'bidding', label: 'Торги', statuses: ['BIDDING'] },
  { value: 'active', label: 'Активные', statuses: ['ASSIGNED', 'IN_PROGRESS', 'PAYMENT_PENDING'] },
  { value: 'completed', label: 'Завершённые', statuses: ['PAID_CARD', 'PAID_CASH', 'COMPLETED'] },
  { value: 'disputed', label: 'Споры', statuses: ['DISPUTED', 'REFUNDED', 'CANCELED'] },
];

const STATUS_OPTIONS: { value: OrderStatus; label: string }[] = [
  { value: 'BIDDING', label: ORDER_STATUS_LABELS.BIDDING },
  { value: 'ASSIGNED', label: ORDER_STATUS_LABELS.ASSIGNED },
  { value: 'IN_PROGRESS', label: ORDER_STATUS_LABELS.IN_PROGRESS },
  { value: 'PAYMENT_PENDING', label: ORDER_STATUS_LABELS.PAYMENT_PENDING },
  { value: 'PAID_CARD', label: ORDER_STATUS_LABELS.PAID_CARD },
  { value: 'PAID_CASH', label: ORDER_STATUS_LABELS.PAID_CASH },
  { value: 'COMPLETED', label: ORDER_STATUS_LABELS.COMPLETED },
  { value: 'CANCELED', label: ORDER_STATUS_LABELS.CANCELED },
  { value: 'REFUNDED', label: ORDER_STATUS_LABELS.REFUNDED },
  { value: 'DISPUTED', label: ORDER_STATUS_LABELS.DISPUTED },
];

export function OrdersPage() {
  const [activeTab, setActiveTab] = useState<TabValue>('all');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [resolveOrderId, setResolveOrderId] = useState<string | null>(null);
  const { toast } = useToast();

  // Get current tab config
  const currentTab = TABS.find((t) => t.value === activeTab) || TABS[0];

  // Determine status filter based on tab and manual filter
  const getStatusFilter = (): OrderStatus | undefined => {
    if (statusFilter !== 'all') {
      return statusFilter;
    }
    if (currentTab.statuses && currentTab.statuses.length === 1) {
      return currentTab.statuses[0];
    }
    return undefined;
  };

  // Fetch orders
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useOrders({
    status: getStatusFilter(),
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    limit: 50,
  });

  // Mutations
  const assignMutation = useAssignMaster();
  const disputeMutation = useDisputeOrder();

  // Filter orders by tab statuses (for multi-status tabs)
  const filteredOrders = data?.data.filter((order) => {
    if (!currentTab.statuses || currentTab.statuses.length <= 1) {
      return true;
    }
    return currentTab.statuses.includes(order.status);
  }) || [];

  // Handlers
  const handleAssignMaster = async (orderId: string, masterId: string) => {
    try {
      await assignMutation.mutateAsync({ orderId, masterId });
      toast({
        title: 'Мастер назначен',
        description: 'Заказ успешно назначен мастеру',
      });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
    } catch (err) {
      toast({
        title: 'Ошибка',
        description: err instanceof Error ? err.message : 'Не удалось назначить мастера',
        variant: 'destructive',
      });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
    }
  };

  const handleDispute = async (orderId: string, reason: DisputeReason, comment?: string) => {
    try {
      await disputeMutation.mutateAsync({ orderId, reason, comment });
      toast({
        title: 'Спор открыт',
        description: 'Заказ помечен как спорный',
      });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
    } catch (err) {
      toast({
        title: 'Ошибка',
        description: err instanceof Error ? err.message : 'Не удалось открыть спор',
        variant: 'destructive',
      });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
    }
  };

  const handleResolveDispute = (orderId: string) => {
    setResolveOrderId(orderId);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = statusFilter !== 'all' || dateFrom || dateTo;
  const total = data?.pagination?.total || 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Заказы</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            className={hasActiveFilters ? 'text-primary' : ''}
          >
            <Filter className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Фильтры</p>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-3 w-3 mr-1" />
                  Сбросить
                </Button>
              )}
            </div>

            {/* Status Filter */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Статус</label>
              <Select
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value as OrderStatus | 'all')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Все статусы" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Filters */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">От</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">До</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.value;
          return (
            <Button
              key={tab.value}
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              className="shrink-0"
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.label}
              {tab.value === 'bidding' && isActive && total > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {filteredOrders.length}
                </Badge>
              )}
            </Button>
          );
        })}
      </div>

      {/* Content */}
      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : isError ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-2" />
            <p className="text-destructive font-medium">Ошибка загрузки</p>
            <p className="text-sm text-muted-foreground mb-4">
              {error?.message || 'Не удалось загрузить данные'}
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Повторить
            </Button>
          </CardContent>
        </Card>
      ) : filteredOrders.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-muted-foreground" />
              {currentTab.label}
            </CardTitle>
            <CardDescription>
              {activeTab === 'all' && 'Все заказы в системе'}
              {activeTab === 'bidding' && 'Заказы на торгах'}
              {activeTab === 'active' && 'Активные заказы'}
              {activeTab === 'completed' && 'Завершённые заказы'}
              {activeTab === 'disputed' && 'Спорные заказы'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
              <ClipboardList className="mb-2 h-12 w-12 opacity-50" />
              <p>Нет заказов</p>
              <p className="text-sm">
                {hasActiveFilters
                  ? 'Попробуйте изменить фильтры'
                  : 'Заказы появятся здесь'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Stats */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Всего: {filteredOrders.length}</span>
            {isFetching && (
              <span className="flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Обновление...
              </span>
            )}
          </div>

          {/* Order Cards */}
          {filteredOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onAssignMaster={handleAssignMaster}
              onDispute={handleDispute}
              onResolveDispute={handleResolveDispute}
              isAssigning={
                assignMutation.isPending &&
                assignMutation.variables?.orderId === order.id
              }
              isDisputing={
                disputeMutation.isPending &&
                disputeMutation.variables?.orderId === order.id
              }
            />
          ))}

          {/* Pagination info */}
          {data?.pagination && data.pagination.totalPages > 1 && (
            <div className="text-center text-sm text-muted-foreground">
              Страница {data.pagination.page} из {data.pagination.totalPages}
            </div>
          )}
        </div>
      )}

      <ResolveDisputeModal
        orderId={resolveOrderId}
        open={resolveOrderId !== null}
        onOpenChange={(open) => { if (!open) setResolveOrderId(null); }}
        onResolved={() => {
          toast({
            title: 'Спор решён',
            description: 'Решение по спору успешно применено',
          });
        }}
      />
    </div>
  );
}
